import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Button, Avatar } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";

const CARD_WIDTH = 280;
const GAP = 24;

const OtherTalksSection = ({ talks, activeTalkId, onSelectTalk }) => {
  const containerRef = useRef(null);

  // Dynamic filter state
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  // Dynamic categories from talks
  const categories = ["ALL", ...new Set(talks.map((t) => t.filtercat))];

  // Filter logic (category + exclude active talk)
  // const otherTalks = talks
  //   .filter((t) =>
  //     selectedCategory === "ALL" ? true : t.filtercat === selectedCategory
  //   )
  //   .filter((t) => t.id !== activeTalkId);

  // Filter logic (category + active talk)
  const otherTalks = talks.filter((t) =>
    selectedCategory === "ALL" ? true : t.filtercat === selectedCategory
  );

  const [activePage, setActivePage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [isScrollable, setIsScrollable] = useState(false);
  const [visibleCards, setVisibleCards] = useState(1);

  useEffect(() => {
    const calculate = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;

      const totalWidth =
        otherTalks.length * CARD_WIDTH + (otherTalks.length - 1) * GAP;

      const canScroll = totalWidth > containerWidth;
      setIsScrollable(canScroll);

      if (!canScroll) {
        setVisibleCards(otherTalks.length || 1);
        setPageCount(1);
        setActivePage(0);
        return;
      }

      const cardsFit = Math.floor(containerWidth / (CARD_WIDTH + GAP)) || 1;

      setVisibleCards(cardsFit);
      setPageCount(Math.ceil(otherTalks.length / cardsFit));
      setActivePage(0);
    };

    calculate();
    window.addEventListener("resize", calculate);
    return () => window.removeEventListener("resize", calculate);
  }, [otherTalks.length]);

  const scrollToPage = (page) => {
    if (!containerRef.current) return;

    setActivePage(page);

    containerRef.current.scrollTo({
      left: page * visibleCards * (CARD_WIDTH + GAP),
      behavior: "smooth",
    });
  };

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 2,
        }}
        className="other-talks-row-box"
      >
        <Typography className="other-talks-title">
          Other Talks <span> (4/11) </span>
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }} className="other-talks-cat-box">
          {categories.map((cat) => (
            <Button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              variant="contained"
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: 0,
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                opacity: selectedCategory === cat ? 1 : 0.6,
                "&:hover": { backgroundColor: "#000" },
              }}
            >
              {cat}
            </Button>
          ))}
        </Box>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          gap: `${GAP}px`,
          overflowX: isScrollable ? "auto" : "hidden",
          scrollSnapType: isScrollable ? "x proximity" : "none",
          pb: 3,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {otherTalks.map((talk) => {
          const thumb = talk.episodes?.[0]?.thumbnail || talk.thumbnail;

          return (
            <Box
              key={talk.id}
              onClick={() => onSelectTalk(talk)}
              sx={{
                cursor: "pointer",
                width: CARD_WIDTH,
                minWidth: CARD_WIDTH,
                scrollSnapAlign: "start",
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                boxShadow:
                  "0 4px 8px rgba(0,0,0,0.12), 0 6px 10px rgba(0,0,0,0.14)",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Box
                  component="img"
                  src={thumb}
                  alt={talk.title}
                  sx={{ width: "100%", height: 170, objectFit: "cover" }}
                  className="thumbnail-overlay"
                  style={{ filter: "brightness(0.5)" }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      backgroundColor: "#A100FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderLeft: "12px solid #fff",
                        ml: "3px",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 2 }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#A100FF",
                    lineHeight: 1.4,
                    mb: 0.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {talk.title}
                </Typography>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }}
                >
                  <Typography sx={{ fontSize: 12 }}>Speaker:</Typography>
                  <Avatar
                    src={talk.speakerImage}
                    sx={{ width: 18, height: 18 }}
                  />
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    {talk.speakerName}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#666",
                    lineHeight: 1.5,
                    mb: 2,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {talk.courseDescription}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "#666",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    {talk.duration}
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <EventIcon sx={{ fontSize: 16 }} />
                    {talk.courseDate}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {isScrollable && pageCount > 1 && (
        <Box
          sx={{ display: "flex", justifyContent: "center", gap: 1 }}
          className="scrollable-option"
        >
          {Array.from({ length: pageCount }).map((_, index) => (
            <Box
              key={index}
              onClick={() => scrollToPage(index)}
              sx={{
                width: activePage === index ? 22 : 8,
                height: 8,
                borderRadius: 0,
                backgroundColor: activePage === index ? "#000" : "#bbb",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OtherTalksSection;
